import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FilaPaciente } from './FilaPaciente';

import {
    ContenedorPacientes,
    EncabezadoPacientes,
    TabsPacientes,
    BotonTabPaciente,
    CantidadPacientes,
    EspacioFlexible,
    BotonNuevoPaciente,
    ContenedorFiltros,
    CampoFiltro,
    InputFiltro,
    SelectFiltro,
    TablaContenedor,
    EncabezadoTablaPacientes,
    TituloColumna,
    SinResultados
} from '../../Css/StyledListadoPacientes';

import { cambiarArchivadoPacienteAction } from '../../../actions/cargarPacientesActions';
import { setearIdPacienteAction } from '../../../actions/setearIdPacienteActions';
import { resetearConfigPacienteAction } from '../../../actions/resetStoreActions';

import useReturnInitialState from '../../../Hooks/useReturnInitialState';


export const TablaPacientes = ({ pacientes, config }) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [returnState] = useReturnInitialState();


    /* ----------------------------------------------------
       VISTA
    ---------------------------------------------------- */

    const [vista, setVista] = useState('activos');


    /* ----------------------------------------------------
       FILTROS
    ---------------------------------------------------- */

    const [busqueda, setBusqueda] = useState('');
    const [servicio, setServicio] = useState('');
    const [cobertura, setCobertura] = useState('');
    const [edad, setEdad] = useState('');


    /* ----------------------------------------------------
       ORDEN
    ---------------------------------------------------- */

    const [orden, setOrden] = useState({
        campo: 'apellido',
        direccion: 'asc'
    });


    /* ----------------------------------------------------
       LISTAS PARA SELECTS
    ---------------------------------------------------- */

    const serviciosDisponibles = useMemo(() => {

        return [
            ...new Set(
                pacientes
                    .map((paciente) => paciente.servicio)
                    .filter(Boolean)
            )
        ].sort();

    }, [pacientes]);


    const coberturasDisponibles = useMemo(() => {

        return [
            ...new Set(
                pacientes
                    .map((paciente) => paciente.cobertura_medica)
                    .filter(Boolean)
            )
        ].sort();

    }, [pacientes]);


    /* ----------------------------------------------------
       PACIENTES ACTIVOS / ARCHIVADOS
    ---------------------------------------------------- */

    const pacientesActivos = useMemo(() => {

        return pacientes.filter((paciente) => {

            // Mientras el backend todavía no envíe "archivado",
            // undefined se considera paciente activo.
            return paciente.archivado !== true;

        });

    }, [pacientes]);


    const pacientesArchivados = useMemo(() => {

        return pacientes.filter(
            (paciente) => paciente.archivado === true
        );

    }, [pacientes]);


    /* ----------------------------------------------------
       FILTROS + ORDEN
    ---------------------------------------------------- */

    const pacientesVisibles = useMemo(() => {

        const listaBase =
            vista === 'activos'
                ? pacientesActivos
                : pacientesArchivados;


        const listaFiltrada = listaBase.filter((paciente) => {

            /* Búsqueda */

            if (busqueda.trim()) {

                const textoBusqueda = busqueda
                    .trim()
                    .toLowerCase();


                const nombreCompleto = `
                    ${paciente.apellido || ''}
                    ${paciente.nombre || ''}
                `.toLowerCase();


                if (!nombreCompleto.includes(textoBusqueda)) {
                    return false;
                }
            }


            /* Servicio */

            if (
                servicio &&
                paciente.servicio !== servicio
            ) {
                return false;
            }


            /* Cobertura */

            if (
                cobertura &&
                paciente.cobertura_medica !== cobertura
            ) {
                return false;
            }


            /* Edad */

            if (edad) {

                const edadPaciente = Number(paciente.edad);

                if (
                    edad === '0-6' &&
                    !(edadPaciente >= 0 && edadPaciente <= 6)
                ) {
                    return false;
                }


                if (
                    edad === '7-11' &&
                    !(edadPaciente >= 7 && edadPaciente <= 11)
                ) {
                    return false;
                }


                if (
                    edad === '12+' &&
                    edadPaciente < 12
                ) {
                    return false;
                }
            }


            return true;
        });


        return [...listaFiltrada].sort((a, b) => {

            let valorA = a[orden.campo] ?? '';
            let valorB = b[orden.campo] ?? '';


            if (typeof valorA === 'string') {
                valorA = valorA.toLowerCase();
            }


            if (typeof valorB === 'string') {
                valorB = valorB.toLowerCase();
            }


            if (valorA < valorB) {
                return orden.direccion === 'asc' ? -1 : 1;
            }


            if (valorA > valorB) {
                return orden.direccion === 'asc' ? 1 : -1;
            }


            return 0;
        });


    }, [
        vista,
        pacientesActivos,
        pacientesArchivados,
        busqueda,
        servicio,
        cobertura,
        edad,
        orden
    ]);


    /* ----------------------------------------------------
       ORDENAR
    ---------------------------------------------------- */

    const ordenarPor = (campo) => {

        setOrden((ordenAnterior) => {

            if (ordenAnterior.campo === campo) {

                return {
                    campo,
                    direccion:
                        ordenAnterior.direccion === 'asc'
                            ? 'desc'
                            : 'asc'
                };
            }


            return {
                campo,
                direccion: 'asc'
            };
        });
    };


    /* ----------------------------------------------------
       INGRESAR AL PACIENTE
    ---------------------------------------------------- */

    const seleccionarPaciente = (paciente) => {

        dispatch(resetearConfigPacienteAction());


        const informacion = {

            payload: {

                id: paciente.id_paciente,
                nombre: paciente.nombre,
                apellido: paciente.apellido
            }
        };


        dispatch(setearIdPacienteAction(informacion));

        navigate('/escritorio/pacientes/paciente');
    };


    /* ----------------------------------------------------
       NUEVO PACIENTE
    ---------------------------------------------------- */

    const nuevoPaciente = () => {

        returnState([
            'estado.error',
            'estado.loading'
        ]);

        navigate('/escritorio/pacientes/nuevo-paciente');
    };


    /* ----------------------------------------------------
       ARCHIVAR / RESTAURAR

       Por ahora dejamos preparado el punto de entrada.
       Después lo conectamos con Redux + backend.
    ---------------------------------------------------- */

    const archivarPaciente = async (paciente) => {

        const resultado = await Swal.fire({

            title: 'Archivar paciente',

            html: `
                <strong>
                    ${paciente.nombre} ${paciente.apellido}
                </strong>
                dejará de aparecer entre tus pacientes activos.
                <br><br>
                No se eliminará ninguna información y podrás
                restaurarlo cuando quieras.
            `,

            showCancelButton: true,

            confirmButtonText:
                'Archivar paciente',

            cancelButtonText:
                'Cancelar',

            confirmButtonColor:
                '#FC8906'
        });


        if (!resultado.isConfirmed) {
            return;
        }


        const respuesta = await dispatch(
            cambiarArchivadoPacienteAction({
                id_paciente:
                    paciente.id_paciente,

                archivado: true,

                config
            })
        );


        if (respuesta?.ok) {

            Swal.fire({
                icon: 'success',
                title: 'Paciente archivado',
                showConfirmButton: false,
                timer: 1400
            });
        } else {

            await Swal.fire({
                icon: 'error',
                title: 'No se pudo archivar el paciente',
                text:
                    respuesta?.error?.msg ||
                    'Ocurrió un error. Intentá nuevamente.',
                confirmButtonText: 'OK'
            });

        }
    };


    const restaurarPaciente = async (paciente) => {

        const resultado = await Swal.fire({

            title: 'Restaurar paciente',

            text: `
                ${paciente.nombre} ${paciente.apellido}
                volverá a aparecer entre tus pacientes activos.
            `,

            showCancelButton: true,

            confirmButtonText:
                'Restaurar',

            cancelButtonText:
                'Cancelar',

            confirmButtonColor:
                '#23AAAF'
        });


        if (!resultado.isConfirmed) {
            return;
        }


        const respuesta = await dispatch(
            cambiarArchivadoPacienteAction({
                id_paciente:
                    paciente.id_paciente,

                archivado: false,

                config
            })
        );


        if (respuesta?.ok) {

            Swal.fire({
                icon: 'success',
                title: 'Paciente restaurado',
                showConfirmButton: false,
                timer: 1400
            });
        } else {

            await Swal.fire({
                icon: 'error',
                title: 'No se pudo restaurar el paciente',
                text:
                    respuesta?.error?.msg ||
                    'Ocurrió un error. Intentá nuevamente.',
                confirmButtonText: 'OK'
            });

        }
    };


    return (

        <ContenedorPacientes>

            {/* --------------------------------------------
                ACTIVOS / ARCHIVADOS / NUEVO
            --------------------------------------------- */}

            <EncabezadoPacientes>

                <TabsPacientes>

                    <BotonTabPaciente
                        type="button"
                        activo={vista === 'activos'}
                        onClick={() => setVista('activos')}
                    >
                        Activos
                    </BotonTabPaciente>


                    <BotonTabPaciente
                        type="button"
                        activo={vista === 'archivados'}
                        onClick={() => setVista('archivados')}
                    >
                        Archivados
                    </BotonTabPaciente>

                </TabsPacientes>


                <CantidadPacientes>

                    {vista === 'activos'
                        ? `${pacientesActivos.length} pacientes activos`
                        : `${pacientesArchivados.length} pacientes archivados`
                    }

                </CantidadPacientes>


                <EspacioFlexible />


                <BotonNuevoPaciente
                    type="button"
                    onClick={nuevoPaciente}
                >
                    + Nuevo paciente
                </BotonNuevoPaciente>

            </EncabezadoPacientes>


            {/* --------------------------------------------
                FILTROS
            --------------------------------------------- */}

            <ContenedorFiltros>

                <CampoFiltro ancho="250px">

                    <InputFiltro
                        type="text"
                        value={busqueda}
                        placeholder="Buscar por apellido o nombre"
                        onChange={(e) =>
                            setBusqueda(e.target.value)
                        }
                    />

                </CampoFiltro>


                <CampoFiltro>

                    <SelectFiltro
                        value={servicio}
                        onChange={(e) =>
                            setServicio(e.target.value)
                        }
                    >
                        <option value="">
                            Servicio
                        </option>

                        {serviciosDisponibles.map(
                            (item) => (
                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>
                            )
                        )}

                    </SelectFiltro>

                </CampoFiltro>


                <CampoFiltro>

                    <SelectFiltro
                        value={cobertura}
                        onChange={(e) =>
                            setCobertura(e.target.value)
                        }
                    >
                        <option value="">
                            Cobertura médica
                        </option>

                        {coberturasDisponibles.map(
                            (item) => (
                                <option
                                    key={item}
                                    value={item}
                                >
                                    {item}
                                </option>
                            )
                        )}

                    </SelectFiltro>

                </CampoFiltro>


                <CampoFiltro>

                    <SelectFiltro
                        value={edad}
                        onChange={(e) =>
                            setEdad(e.target.value)
                        }
                    >
                        <option value="">
                            Edad
                        </option>

                        <option value="0-6">
                            0 a 6 años
                        </option>

                        <option value="7-11">
                            7 a 11 años
                        </option>

                        <option value="12+">
                            12 años o más
                        </option>

                    </SelectFiltro>

                </CampoFiltro>

            </ContenedorFiltros>


            {/* --------------------------------------------
                LISTADO
            --------------------------------------------- */}

            <TablaContenedor>

                <EncabezadoTablaPacientes>

                    <TituloColumna
                        type="button"
                        ordenable
                        onClick={() =>
                            ordenarPor('apellido')
                        }
                    >
                        Paciente
                        {orden.campo === 'apellido' &&
                            (
                                orden.direccion === 'asc'
                                    ? ' ↑'
                                    : ' ↓'
                            )
                        }
                    </TituloColumna>


                    <TituloColumna
                        className="columna-servicio"
                        type="button"
                        ordenable
                        onClick={() =>
                            ordenarPor('servicio')
                        }
                    >
                        Servicio
                        {orden.campo === 'servicio' &&
                            (
                                orden.direccion === 'asc'
                                    ? ' ↑'
                                    : ' ↓'
                            )
                        }
                    </TituloColumna>


                    <TituloColumna
                        className="columna-cobertura"
                        type="button"
                        ordenable
                        onClick={() =>
                            ordenarPor('cobertura_medica')
                        }
                    >
                        Cobertura médica
                        {orden.campo === 'cobertura_medica' &&
                            (
                                orden.direccion === 'asc'
                                    ? ' ↑'
                                    : ' ↓'
                            )
                        }
                    </TituloColumna>


                    <TituloColumna
                        type="button"
                        ordenable
                        onClick={() =>
                            ordenarPor('edad')
                        }
                    >
                        Edad
                        {orden.campo === 'edad' &&
                            (
                                orden.direccion === 'asc'
                                    ? ' ↑'
                                    : ' ↓'
                            )
                        }
                    </TituloColumna>


                    <div />

                </EncabezadoTablaPacientes>


                {pacientesVisibles.length === 0 ? (

                    <SinResultados>

                        {vista === 'archivados' &&
                        pacientesArchivados.length === 0

                            ? 'Todavía no archivaste ningún paciente.'

                            : 'No encontramos pacientes con los filtros seleccionados.'
                        }

                    </SinResultados>

                ) : (

                    pacientesVisibles.map(
                        (paciente, indice) => (

                            <FilaPaciente
                                key={paciente.id_paciente}

                                paciente={paciente}

                                indice={indice}

                                archivado={
                                    paciente.archivado === true
                                }

                                onSeleccionar={
                                    seleccionarPaciente
                                }

                                onArchivar={
                                    archivarPaciente
                                }

                                onRestaurar={
                                    restaurarPaciente
                                }
                            />
                        )
                    )
                )}

            </TablaContenedor>

        </ContenedorPacientes>
    );
};